import React, { useState } from "react";
import {
  User,
  Phone,
  Calendar,
  X,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { supabase } from "../../lib/supabase";
import { getContrastTextColor } from "../../lib/color-utils";

interface WaitlistJoinModalProps {
  businessId: string;
  serviceType: string;
  targetDate: string;
  /**
   * The specific unavailable time the client clicked "Waitlist" under, e.g.
   * "14:00". The whole point of clicking a time slot rather than a bare date
   * is that the client wants THAT time -- so the window defaults to exactly
   * that, narrow, rather than the full business day. They can still widen it.
   * Optional so the modal still works if a future caller has no single slot
   * to anchor to (e.g. a "waitlist for any time this day" entry point).
   */
  targetTime?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  primaryColor?: string;
}

export const WaitlistJoinModal: React.FC<WaitlistJoinModalProps> = ({
  businessId,
  serviceType,
  targetDate,
  targetTime,
  isOpen,
  onClose,
  onSuccess,
  primaryColor = "#2563eb",
}) => {
  const [customerName, setCustomerName] = useState<string>("");
  const [customerPhone, setCustomerPhone] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  // Real-time normalization preview for Jordan numbers
  const getNormalizedPreview = (raw: string) => {
    const cleaned = raw.replace(/\D/g, "");
    if (/^07[789]/.test(cleaned)) {
      return (
        "+962 " +
        cleaned.substring(1, 3) +
        " " +
        cleaned.substring(3, 6) +
        " " +
        cleaned.substring(6)
      );
    }
    if (/^7[789]/.test(cleaned)) {
      return (
        "+962 " +
        cleaned.substring(0, 2) +
        " " +
        cleaned.substring(2, 5) +
        " " +
        cleaned.substring(5)
      );
    }
    if (/^962/.test(cleaned)) {
      return "+" + cleaned;
    }
    return cleaned ? "+" + cleaned : "";
  };

  // Clients see a day they recognise, not a database date.
  const friendlyDate = (() => {
    try {
      return format(parseISO(targetDate), "EEEE, d MMM");
    } catch {
      return targetDate;
    }
  })();

  // "14:00" -> "2:00 PM". Shown so the client sees confirmation this is for
  // the exact time they clicked, not a vague window they now have to define.
  const friendlyTime = (() => {
    if (!targetTime) return null;
    const [h, m] = targetTime.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerName.trim() || !customerPhone.trim()) {
      setErrorMessage(
        "Please add your name and phone number so we can reach you.",
      );
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      const { error } = await supabase.from("waitlist_entries").insert({
        business_id: businessId,
        service_type: serviceType,
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim(),
        target_date: targetDate,
        preferred_time_start: targetTime || "09:00",
        preferred_time_end: targetTime || "18:00",
        status: "waiting",
      });

      if (error) {
        // The dedup index used to be per-day while this modal has always been
        // per-time, so asking for a second time on the same day was rejected
        // with "already on the list for this day" -- which was both a refusal
        // of something reasonable and, once the entry went stale, a lie. The
        // index now includes the requested time, so a 23505 here genuinely
        // means this exact slot.
        if (error.code === "23505") {
          throw new Error(
            friendlyTime
              ? `You're already on the list for ${friendlyTime} — we'll message you if it frees up.`
              : "You're already on the list for this time — we'll message you if it frees up.",
          );
        }
        if (error.message?.includes("waitlist_disabled")) {
          throw new Error(
            "This salon isn't taking waitlist requests right now. Try picking another time.",
          );
        }
        if (error.message?.includes("rate_limit_exceeded")) {
          throw new Error(
            "You've tried a few times just now. Please wait a couple of minutes and try again.",
          );
        }
        throw new Error(
          "We couldn't add you to the list just now. Please try again in a moment.",
        );
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "We couldn't add you to the list just now. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // text-base (16px) on mobile prevents iOS Safari from auto-zooming the viewport upon focus
  const fieldClass =
    "w-full rounded-[10px] border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-base sm:text-xs text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 p-0 sm:p-4 backdrop-blur-sm animate-fade-in overflow-y-auto overscroll-contain">
      <div className="w-full max-w-md rounded-t-[24px] sm:rounded-[16px] bg-white p-5 sm:p-6 shadow-2xl border border-slate-200/80 max-h-[92dvh] sm:max-h-[85vh] flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
        {/* Mobile Pull/Drag Indicator */}
        <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-3 sm:hidden shrink-0" />

        <div className="flex items-start justify-between border-b border-slate-100 pb-3 sm:pb-4 shrink-0">
          <div className="pr-4">
            <h3 className="text-base sm:text-lg font-bold text-slate-900">
              Join the waitlist
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {friendlyTime
                ? `${friendlyTime} is taken. We'll message you if it frees up.`
                : "This time is taken. We'll message you if it frees up."}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors shrink-0 -mr-1 -mt-1 touch-manipulation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {errorMessage && (
          <div className="mt-3 sm:mt-4 flex items-center gap-2 rounded-[10px] bg-red-50 p-3 text-xs text-red-700 border border-red-200 shrink-0">
            <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
            <span>{errorMessage}</span>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="mt-3 sm:mt-4 space-y-3.5 sm:space-y-4 overflow-y-auto flex-1 pr-0.5 overscroll-contain"
        >
          {/* What they're waiting for */}
          <div className="rounded-[10px] bg-slate-50 p-3 text-xs border border-slate-100 space-y-1">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <Calendar className="h-4 w-4 text-slate-400" />
              <span>{serviceType}</span>
            </div>
            <p className="text-slate-500 pl-6">
              {friendlyDate}
              {friendlyTime ? ` at ${friendlyTime}` : ""}
            </p>
          </div>

          {/* Name Field */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Your name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                type="text"
                required
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g. Layla Al-Ahmad"
                className={fieldClass}
              />
            </div>
          </div>

          {/* Phone Field */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Your WhatsApp number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                type="tel"
                required
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="079 123 4567"
                className={fieldClass}
              />
            </div>
            {customerPhone && (
              <p className="mt-1 text-[11px] text-slate-500">
                We&apos;ll message you on{" "}
                <span className="font-mono text-slate-700">
                  {getNormalizedPreview(customerPhone)}
                </span>
              </p>
            )}
          </div>

          {/* What we'll actually do */}
          <div className="flex items-center gap-2 rounded-[10px] bg-slate-50 p-2.5 text-[11px] text-slate-500 border border-slate-100">
            <ShieldCheck className="h-4 w-4 shrink-0 text-slate-400" />
            <span>
              {friendlyTime
                ? `One message, only if ${friendlyTime} frees up. Nothing else.`
                : "One message, only if a slot frees up on this day. Nothing else."}
            </span>
          </div>

          {/* Submit Action */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              backgroundColor: primaryColor,
              color: getContrastTextColor(primaryColor),
            }}
            className="w-full flex items-center justify-center gap-2 rounded-[12px] sm:rounded-[10px] py-3.5 sm:py-3 text-sm sm:text-xs font-bold sm:font-semibold disabled:opacity-50 transition-opacity shadow-sm min-h-[44px] touch-manipulation"
          >
            {submitting ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                <span>Add me to the list</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
